import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

// PKCE Helper Functions
const base64URLEncode = (buffer: Uint8Array): string => {
  return btoa(String.fromCharCode(...buffer))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(hash));
};

export const AuthStartMicrosoft = () => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initPKCEFlow = async () => {
      try {
        const clientId = '5eaa225e-61a5-407a-93b0-20d0eb819191';
        
        // Generate PKCE parameters
        const codeVerifier = generateCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        
        console.log('[AuthStartMicrosoft] PKCE flow started');
        console.log('[AuthStartMicrosoft] Code verifier:', codeVerifier.substring(0, 10) + '...');
        
        // Store verifier in sessionStorage (popup context)
        const storageKey = 'pkce_verifier_direct_login';
        sessionStorage.setItem(storageKey, codeVerifier);
        console.log('[AuthStartMicrosoft] Stored verifier in sessionStorage');
        
        const redirectUri = encodeURIComponent(`${window.location.origin}/auth-callback-microsoft`);
        const scopes = encodeURIComponent('openid profile User.Read Contacts.ReadWrite MailboxSettings.ReadWrite');
        
        // PKCE Authorization Code Flow
        const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
          `client_id=${clientId}` +
          `&response_type=code` +
          `&redirect_uri=${redirectUri}` +
          `&scope=${scopes}` +
          `&response_mode=query` +
          `&code_challenge=${codeChallenge}` +
          `&code_challenge_method=S256`;
        
        console.log('[AuthStartMicrosoft] Redirecting to Microsoft OAuth with PKCE...');
        window.location.href = authUrl;
      } catch (err) {
        console.error('[AuthStartMicrosoft] PKCE flow error:', err);
        setError('Failed to initialize authentication');
      }
    };

    initPKCEFlow();
  }, []);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-sm text-muted-foreground">Redirecting to Microsoft login...</p>
      </div>
    </div>
  );
};
